import { Controller, useForm } from 'react-hook-form';
import {
  BillingRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { states, countryOptions } from '../../../shared/stripe-address-utils';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { BillingKeys, updateCreditCardInfo } from '../../../api/billing-api';
import { ModuleList } from '../../../shared/analytics/module-list';
import { handleErrors } from '../../../utils/errors';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import * as analytics from '../../../shared/analytics/analytics.json';
import {
  BillingInformationModel,
  StripeCreditCardPaymentSourceModel,
} from '../../../api/billing-types';

import dayjs from 'dayjs';
import AvatarChip from '../../../shared/avatar-chip';

import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Save from '@mui/icons-material/Save';

interface PaymentMethodsEditCreditCardFormProps {
  billingRoles: string[];
  changeLogRoles: string[];
  paymentMethod: StripeCreditCardPaymentSourceModel;
  billingInfoModel: BillingInformationModel;
}

export default function PaymentMethodsEditCreditCardForm({
  billingRoles,
  changeLogRoles,
  paymentMethod,
  billingInfoModel,
}: PaymentMethodsEditCreditCardFormProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const currentYear = dayjs().year();

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<StripeCreditCardPaymentSourceModel>({
    defaultValues: {
      ...paymentMethod,
      address_country:
        paymentMethod.address_country == 'US'
          ? 'United States'
          : paymentMethod.address_country,
      exp_month: paymentMethod.exp_month.toString().padStart(2, '0'),
      metadata: {
        ...paymentMethod.metadata,
        nickName: paymentMethod.metadata?.nickName || '',
      },
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (data: StripeCreditCardPaymentSourceModel) =>
      updateCreditCardInfo(
        billingInfoModel,
        {
          ...data,
          exp_month: parseInt(data.exp_month.toString()),
        },
        changeLogMessage
      ),
    onSuccess: (returnedData, data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Payment method updated successfully!',
      });

      reset(data);

      queryClient.setQueryData([BillingKeys.CustomerBillingInformation], {
        ...billingInfoModel,
        stripeSourceModels: billingInfoModel.stripeSourceModels.map((source) =>
          source.id === returnedData.id ? data : source
        ),
      });

      setChangeLogMessage('');
      setOpenChangeLogDialog(false);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating the payment method. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.billing.payments.edit_payment_method, {
        module: ModuleList.modules.billing.name,
        section: ModuleList.modules.billing.sections.payments,
      });
    },
  });

  const onSubmit = (data: StripeCreditCardPaymentSourceModel) => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        BillingRoles.canEditPaymentMethods.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const canEditPaymentMethods = isAbleToSeeUIElement(
    billingRoles,
    BillingRoles.canEditPaymentMethods.value
  );

  const selectedCountry = watch('address_country');
  const hasCreatedDate = paymentMethod.metadata?.createdDate !== '0';
  const hasCreatedBy = !!paymentMethod.metadata?.createdBy;

  return (
    <Box>
      <AccordionDetails>
        <Stack alignItems="flex-end" sx={{ width: '100%', my: 0.5 }}>
          <Typography variant="caption">
            {hasCreatedDate
              ? `created on ${dayjs(paymentMethod.metadata?.createdDate).format(
                  'MM/DD/YYYY'
                )}`
              : ''}
            {hasCreatedDate && hasCreatedBy && ' by '}
            {hasCreatedBy && (
              <AvatarChip
                email={paymentMethod.metadata?.createdBy || ''}
                type="User"
              />
            )}
          </Typography>
        </Stack>
        <Divider sx={{ mt: 1, mb: 2 }} />
        <Tooltip
          arrow
          title={
            canEditPaymentMethods
              ? ''
              : `You need the permission "${BillingRoles.canEditPaymentMethods.name}"  to edit this payment method.`
          }
        >
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
            >
              <Controller
                name="exp_month"
                rules={{
                  required: `Please provide a month.`,
                  min: 1,
                  max: 12,
                  pattern: {
                    value: /^(0[1-9]|1[0-2])$/,
                    message: 'Month must be in MM format (01-12)',
                  },
                }}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Month (MM)"
                    error={!!errors.exp_month}
                    helperText={errors.exp_month?.message || ' '}
                    inputProps={{ maxLength: 2 }}
                    placeholder="MM"
                    size="small"
                    sx={{ width: 125 }}
                    disabled={!canEditPaymentMethods}
                    required
                    FormHelperTextProps={{
                      sx: { textWrap: 'nowrap' },
                    }}
                  />
                )}
              />
              <Controller
                name="exp_year"
                control={control}
                rules={{
                  required: `Please provide a year.`,
                  min: currentYear,
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Year (YYYY)"
                    error={!!errors.exp_year}
                    helperText={errors.exp_year?.message || ' '}
                    inputProps={{ maxLength: 4 }}
                    placeholder="YYYY"
                    size="small"
                    sx={{ width: 160 }}
                    disabled={!canEditPaymentMethods}
                    required
                    FormHelperTextProps={{
                      sx: { textWrap: 'nowrap' },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{ xs: 12, md: 12 }}
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <Controller
                name={'address_line1'}
                control={control}
                rules={{
                  required: `Please provide a street address.`,
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{
                      id: 'address',
                    }}
                    InputLabelProps={{
                      htmlFor: 'address',
                    }}
                    label="Address Line 1"
                    fullWidth
                    required
                    autoComplete="address-line1"
                    error={!!errors.address_line1}
                    helperText={
                      errors.address_line1 ? errors.address_line1.message : ' '
                    }
                    disabled={!canEditPaymentMethods}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{ xs: 12, md: 12 }}
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <Controller
                name={'address_city'}
                control={control}
                rules={{
                  required: `Please provide a city.`,
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{
                      id: 'city',
                    }}
                    InputLabelProps={{
                      htmlFor: 'city',
                    }}
                    label="City"
                    fullWidth
                    required
                    autoComplete="address-level2"
                    error={!!errors.address_city}
                    helperText={
                      errors.address_city ? errors.address_city.message : ' '
                    }
                    disabled={!canEditPaymentMethods}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{ xs: 12, md: 12 }}
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              {selectedCountry === 'United States' ||
              selectedCountry === 'US' ? (
                <Controller
                  name="address_state"
                  control={control}
                  rules={{
                    required: `Please provide a state.`,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      inputProps={{
                        id: 'state',
                      }}
                      InputLabelProps={{
                        htmlFor: 'state',
                      }}
                      label="State"
                      fullWidth
                      required
                      autoComplete="address-level1"
                      select
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            style: {
                              maxHeight: 450,
                            },
                          },
                        },
                      }}
                      error={!!errors.address_state}
                      helperText={
                        errors.address_state
                          ? errors.address_state.message
                          : ' '
                      }
                      disabled={!canEditPaymentMethods}
                    >
                      {states.map((state) => (
                        <MenuItem key={state.value} value={state.value}>
                          {state.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              ) : (
                <Controller
                  name="address_state"
                  rules={{
                    required: `Please provide a province.`,
                  }}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      inputProps={{
                        id: 'state',
                      }}
                      InputLabelProps={{
                        htmlFor: 'state',
                      }}
                      label="Province"
                      fullWidth
                      autoComplete="address-level1"
                      error={!!errors.address_state}
                      helperText={
                        errors.address_state
                          ? errors.address_state.message
                          : ' '
                      }
                      disabled={!canEditPaymentMethods}
                    />
                  )}
                />
              )}
            </Grid>

            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <Controller
                name={'address_zip'}
                control={control}
                rules={{
                  required: `Please provide a zip code.`,
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{
                      id: 'postal_code',
                    }}
                    InputLabelProps={{
                      htmlFor: 'postal_code',
                    }}
                    label="Zip / Postal Code"
                    fullWidth
                    required
                    error={!!errors.address_zip}
                    helperText={
                      errors.address_zip ? errors.address_zip.message : ' '
                    }
                    autoComplete="postal-code"
                    disabled={!canEditPaymentMethods}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <Controller
                name={'address_country'}
                control={control}
                rules={{
                  required: `Please provide a country.`,
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);

                      if (
                        e.target.value === 'United States' ||
                        getValues('address_country') === 'United States'
                      ) {
                        setValue('address_state', '');
                      }
                    }}
                    inputProps={{
                      id: 'country',
                    }}
                    InputLabelProps={{
                      htmlFor: 'country',
                    }}
                    label="Country/Region"
                    fullWidth
                    required
                    error={!!errors.address_country}
                    helperText={
                      errors.address_country
                        ? errors.address_country.message
                        : ' '
                    }
                    autoComplete="country"
                    select
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: 450,
                          },
                        },
                      },
                    }}
                    disabled={!canEditPaymentMethods}
                  >
                    {countryOptions.map((country) => (
                      <MenuItem key={country} value={country}>
                        {country}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <Controller
                name={'metadata.nickName'}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    inputProps={{
                      id: 'nickName',
                    }}
                    InputLabelProps={{
                      htmlFor: 'nickName',
                    }}
                    label="Nick Name"
                    fullWidth
                    disabled={!canEditPaymentMethods}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Tooltip>
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => mutate(getValues())}
          mutateLoading={isPending}
        />
      </AccordionDetails>
      <AccordionActions>
        <Button
          variant="contained"
          color="primary"
          loading={isPending}
          disabled={!isDirty}
          onClick={handleSubmit(onSubmit)}
          endIcon={<Save />}
        >
          Save
        </Button>
      </AccordionActions>
    </Box>
  );
}
